// The Catalog's Sources view (design/SOURCE_CATALOG_DESIGN.md section 7):
// every load the catalog records, grouped by sheet, with the mapping each was
// read with as a link. It manages sources and shows no table data.

import { Fragment, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteSource, getImportTemplate, sourceDeleteJobStatus, type ImportTemplate } from '@/lib/api';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import { describeApiError } from '@/lib/apiErrors';
import { columnInWords, when } from '@/lib/catalogSources';
import { useApiSession } from '@/lib/useApiSession';
import { useCatalogSources, type CatalogLoad, type CatalogSourceGroup } from '@/lib/useCatalog';
import { Button, buttonVariants } from '@/components/ui/Button';
import { Message } from '@/components/ui/Message';

const count = (n: number | null) => (n == null ? '' : n.toLocaleString());

function saveAsFile(mapping: ImportTemplate) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(mapping, null, 2)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${mapping.name.replace(/[^\p{L}\p{N}_-]+/gu, '_')}.mapping.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function MappingPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { token } = useApiSession();
  const mapping = useQuery({
    queryKey: ['import-template', id],
    enabled: !!token,
    queryFn: async () => {
      const r = await getImportTemplate(id, token ?? '');
      if (!r.ok) throw new Error(r.status === 404 ? 'This mapping has been deleted.' : r.message);
      return r.data;
    },
  });
  return (
    <section aria-label="Mapping" className="rounded-lg border border-border bg-card p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-tres-text-primary">
          {mapping.data ? `Mapping ${mapping.data.name}` : 'Mapping'}
        </h2>
        <div className="flex gap-2">
          {mapping.data && (
            <Button type="button" variant="secondary" size="sm" onClick={() => mapping.data && saveAsFile(mapping.data)}>
              Download the file
            </Button>
          )}
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
      {mapping.isLoading && <p className="text-sm text-tres-text-secondary">Reading the mapping…</p>}
      {mapping.error && (
        <Message kind="warning" density="compact">
          {(mapping.error as Error).message}
        </Message>
      )}
      {mapping.data && (
        <>
          <p className="mb-2 text-xs text-tres-text-secondary">
            Last changed {when(mapping.data.updated_at)}. It reads any sheet with these columns, or these and more.
          </p>
          <table className="tres-table text-xs">
            <thead>
              <tr>
                <th>Column</th>
                <th>What it is</th>
              </tr>
            </thead>
            <tbody>
              {mapping.data.spec.columns.map((c) => (
                <tr key={c.column}>
                  <td className="font-mono">{c.column}</td>
                  <td>{columnInWords(c)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function mappingCell(load: CatalogLoad, group: CatalogSourceGroup, open: (id: string) => void) {
  if (load.kind !== 'sheet') return null;
  const id = load.mapping_id ?? group.mapping?.id;
  if (!id) return <span className="text-tres-text-secondary">not saved</span>;
  const name = load.mapping_name ?? group.mapping?.name ?? 'mapping';
  const current = group.mapping?.id === id ? group.mapping.hash : null;
  return (
    <>
      <button type="button" className={buttonVariants({ variant: 'link', size: 'sm' })} onClick={() => open(id)}>
        {name}
      </button>
      {!load.mapping_id && <span className="ml-1 text-tres-text-secondary">saved after this load</span>}
      {load.mapping_id && current && load.mapping_hash && current !== load.mapping_hash && (
        <span className="ml-1 text-tres-text-secondary">changed since this load</span>
      )}
    </>
  );
}

/** Where the removal of one load's facts stands, shown on that load's row. */
type Removal = { phase: 'asking' | 'running' | 'done' | 'failed'; text?: string };

export function CatalogSourcesPanel() {
  const sources = useCatalogSources(true);
  const { token } = useApiSession();
  const queryClient = useQueryClient();
  const [mappingId, setMappingId] = useState<string | null>(null);
  const [removals, setRemovals] = useState<Map<string, Removal>>(new Map());
  const setRemoval = (record: string, r: Removal | null) =>
    setRemovals((m) => {
      const next = new Map(m);
      if (r) next.set(record, r);
      else next.delete(record);
      return next;
    });

  // Remove what one load stated: the existing delete by source, narrowed to
  // the load's moment. A fact a later load restated carries that load's
  // moment and stays.
  async function removeLoad(l: CatalogLoad) {
    if (!l.imported_at || !token) return;
    setRemoval(l.record, { phase: 'running', text: 'Removing…' });
    const started = await deleteSource(l.source, token, l.imported_at);
    if (!started.ok) {
      setRemoval(l.record, { phase: 'failed', text: `Not removed: ${started.message}` });
      return;
    }
    for (;;) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const st = await sourceDeleteJobStatus(started.data.job_id, token);
      if (!st.ok) {
        setRemoval(l.record, { phase: 'failed', text: `The removal's progress could not be read: ${st.message}` });
        return;
      }
      if (st.data.state === 'running') {
        setRemoval(l.record, { phase: 'running', text: `Removing… ${(st.data.progress.deleted + st.data.progress.stripped).toLocaleString()} statements so far.` });
        continue;
      }
      if (st.data.state === 'failed') {
        setRemoval(l.record, { phase: 'failed', text: `Not removed. ${describeApiError(st.data.http_status ?? 500, st.data.error, st.data.detail)}` });
        return;
      }
      const removed = st.data.result?.statements_deleted ?? 0;
      const kept = st.data.result?.attestations_stripped ?? 0;
      setRemoval(l.record, {
        phase: 'done',
        text: `Removed ${removed.toLocaleString()} statement${removed === 1 ? '' : 's'}${
          kept > 0 ? `; ${kept.toLocaleString()} stay because another source also states them` : ''
        }.`,
      });
      invalidateAfterDataWrite(queryClient);
      return;
    }
  }

  const groups = sources.data?.groups ?? [];
  const loads = groups.reduce((n, g) => n + g.loads.length, 0);

  return (
    <div className="space-y-4">
      {sources.isLoading && <p className="text-sm text-tres-text-secondary">Reading the catalog…</p>}
      {sources.error && (
        <Message kind="warning" density="compact">
          The list of sources could not be read: {(sources.error as Error).message}
        </Message>
      )}
      {sources.data && groups.length === 0 && (
        <Message kind="guidance" density="compact">
          Nothing has been catalogued yet. Every file, sheet and connector load made from Database Management is
          listed here once it has run.
        </Message>
      )}
      {groups.length > 0 && (
        <>
          <p className="text-xs text-tres-text-secondary">
            {loads.toLocaleString()} load{loads === 1 ? '' : 's'} of {groups.length.toLocaleString()} source
            {groups.length === 1 ? '' : 's'}. Loads of one sheet are listed together, whatever the file was called.
          </p>
          <div className="overflow-x-auto">
            <table className="tres-table text-xs" aria-label="Sources">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>Source</th>
                  <th>Loaded</th>
                  <th>By</th>
                  <th>Rows</th>
                  <th>Statements written</th>
                  <th>Mapping</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <Fragment key={g.shape_hash ?? `name:${g.name}`}>
                    <tr aria-label={`Source ${g.name}`}>
                      <td />
                      <td colSpan={5} className="font-semibold">
                        {g.name}
                        <span className="ml-2 font-normal text-tres-text-secondary">
                          {g.loads.length === 1 ? '1 load' : `${g.loads.length} loads`}
                        </span>
                      </td>
                      <td>
                        {g.mapping ? (
                          <span className="text-tres-text-secondary">read with {g.mapping.name}, last changed {when(g.mapping.updated_at)}</span>
                        ) : null}
                      </td>
                    </tr>
                    {g.loads.map((l) => (
                      <tr key={l.record} aria-label={`Load of ${l.source} at ${l.imported_at ?? ''}`}>
                        <td className="whitespace-nowrap">
                          {(() => {
                            const r = removals.get(l.record);
                            if (r?.phase === 'asking') {
                              return (
                                <span className="flex items-center gap-1">
                                  Remove?
                                  <Button type="button" variant="danger" size="sm" onClick={() => void removeLoad(l)}>
                                    Yes
                                  </Button>
                                  <Button type="button" variant="secondary" size="sm" onClick={() => setRemoval(l.record, null)}>
                                    No
                                  </Button>
                                </span>
                              );
                            }
                            if (l.removed_at || r?.phase === 'running' || r?.phase === 'done' || !l.imported_at) return null;
                            return (
                              <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                aria-label={`Remove the facts of the load of ${l.source} at ${l.imported_at}`}
                                onClick={() => setRemoval(l.record, { phase: 'asking' })}
                              >
                                Remove facts
                              </Button>
                            );
                          })()}
                        </td>
                        <td>
                          {/* The name again only when this load arrived under another. */}
                          {l.source !== g.name ? `${l.source} ` : ''}
                          {l.sheet && l.sheet !== 'Sheet1' ? `${l.sheet} ` : ''}
                          <span className="text-tres-text-secondary">{l.kind ?? ''}</span>
                          {l.removed_at && <span className="ml-2 text-tres-text-secondary">facts removed {when(l.removed_at)}</span>}
                          {removals.get(l.record)?.text && (
                            <Message kind={removals.get(l.record)?.phase === 'failed' ? 'warning' : removals.get(l.record)?.phase === 'done' ? 'outcome' : 'guidance'} density="compact" className="mt-1">
                              {removals.get(l.record)?.text}
                            </Message>
                          )}
                        </td>
                        <td>{when(l.imported_at)}</td>
                        <td>{l.loaded_by ?? ''}</td>
                        <td>{count(l.rows)}</td>
                        <td>{count(l.statements)}</td>
                        <td>{mappingCell(l, g, setMappingId)}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {mappingId && <MappingPanel id={mappingId} onClose={() => setMappingId(null)} />}
    </div>
  );
}
